'use client'

import * as React from 'react'

import { cn } from '../lib/utils'
import { Button } from './button'

/**
 * Table — the canonical data table. AGENT: READ THIS BEFORE STYLING A TABLE.
 *
 * You get the finished "Scan / access-point table" look FOR FREE, every time,
 * just by using `<Table><TableHeader>…<TableBody>…</Table>`. The bordered
 * rounded card, cell padding (`px-3 py-2.5`), muted header row, row dividers
 * and row hover are all baked into the defaults below. DO NOT re-style them
 * and DO NOT fight them — no hand-rolled `<table>`, and never strip the
 * padding with host overrides like `[&_td]:px-0` / `[&_th]:px-0` (that is the
 * #1 cause of squashed, colliding columns).
 *
 * WHAT STILL NEEDS ONE LINE PER TABLE (genuinely can't be automatic — the
 * component cannot guess your columns):
 *
 *   • Column widths. A plain HTML table auto-sizes columns, so a wide identity
 *     column hogs space and squeezes the rest. For balanced columns add
 *     `className="table-fixed"` on <Table> and `w-[..]` on the <TableHead>s
 *     (e.g. `w-[44%]` / `w-[22%]`). ~4 short classes, not 30 minutes.
 *
 *   • Right-aligning numeric columns: put `text-right` on that column's
 *     <TableHead> and matching <TableCell>.
 *
 *   • NOTHING, for the pinned action column: the LAST column of every table is
 *     frozen against the right edge by default, so Edit / delete never scroll
 *     out of reach and the other columns slide underneath. Opt a table out
 *     with `pinLastColumn={false}` (e.g. when the last column is just another
 *     data column and the border reads as noise).
 *
 * You can ALSO pass a `pagination` prop (cursor- or offset-based) and the
 * Table renders the canonical prev / label / next footer attached to the
 * bottom of its card — same bar whether the table is the whole page or sits
 * inline in a form. Add `stickyFooter` to keep it visible while scrolling a
 * long list.
 *
 * That's the whole job. Everything else is already consistent app-wide.
 */

/**
 * Cursor pagination — token/link based (prev/next only; no page count).
 * `label` is the centred caption (usually the item name, e.g. "Workflows").
 */
type TableCursorPagination = {
  type: 'cursor'
  hasPrev: boolean
  hasNext: boolean
  onPrev: () => void
  onNext: () => void
  label?: React.ReactNode
}

/**
 * Offset / limit pagination — page-based. Renders the SAME bar as cursor;
 * the centre reads "X–Y of N" when `total` is known, else "Page X of Y"
 * (or "Page X" when neither total nor pageCount is given).
 */
type TableOffsetPagination = {
  type: 'offset'
  /** 1-based current page. */
  page: number
  pageSize: number
  /** Total row count — drives "X–Y of N" and the last-page guard. */
  total?: number
  /** Explicit page count; derived from `total`/`pageSize` when omitted. */
  pageCount?: number
  onPageChange: (page: number) => void
}

type TablePaginationProps = (TableCursorPagination | TableOffsetPagination) & {
  className?: string
}

/**
 * Lets `TableRow` know whether to freeze its last cell without every call site
 * having to mark the column. Default `true` — pinning is the norm, opting out
 * is the exception.
 */
const TableContext = React.createContext<{ pinLastColumn: boolean }>({
  pinLastColumn: true,
})

function Table({
  className,
  containerClassName,
  pagination,
  stickyFooter,
  pinLastColumn = true,
  ...props
}: React.ComponentProps<'table'> & {
  containerClassName?: string
  /**
   * Optional attached pagination footer (cursor- or offset-based). Omit for
   * no footer — the presence of this prop IS the on/off switch.
   */
  pagination?: TablePaginationProps
  /**
   * Pin the pagination footer to the bottom of the scroll area (stays visible
   * while scrolling a long table) instead of sitting at the end of the list.
   * Only meaningful alongside `pagination`.
   */
  stickyFooter?: boolean
  /**
   * Freeze the LAST column against the right edge while the rest scroll
   * underneath. **On by default** — that column is nearly always the actions,
   * and they shouldn't scroll out of reach.
   *
   * Pass `false` when the last column is ordinary data and the pinned border
   * reads as noise.
   */
  pinLastColumn?: boolean
}) {
  return (
    <div
      data-slot="table-container"
      // Bordered rounded card by default so every table reads the same (the
      // "Scan table" look). Drops to full-bleed (no side border, no rounding)
      // only when it sits inside a NARROW `@container` — mobile or a squeezed
      // column — and degrades safely to "always bordered" anywhere without a
      // container context. Pass `containerClassName="border-0 rounded-none"`
      // to opt out entirely.
      className={cn(
        'relative flex w-full flex-col rounded-lg border border-border @max-2xl:rounded-none @max-2xl:border-x-0',
        containerClassName
      )}
    >
      <div className="w-full overflow-x-auto">
        <TableContext.Provider value={{ pinLastColumn }}>
          <table
            data-slot="table"
            className={cn('w-full caption-bottom text-sm', className)}
            {...props}
          />
        </TableContext.Provider>
      </div>
      {pagination ? (
        <TablePagination
          {...pagination}
          className={cn(
            'border-t border-border',
            stickyFooter && 'sticky bottom-0 z-10 bg-background',
            pagination.className
          )}
        />
      ) : null}
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      // No background band — the header reads as distinct from the body via
      // its bottom border alone. Per-CELL, for the same reason as `TableRow`:
      // the pinned last cell paints above a row-level border and would break
      // the line where the column starts.
      className={cn('[&_tr>*]:border-b [&_tr>*]:border-border', className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      // Dividers are per-cell (see `TableRow`), so the last row is cleared
      // per-cell too and the table ends flush.
      className={cn('[&_tr:last-child>*]:border-b-0', className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  )
}

/**
 * Pinned (frozen) column. Put `pinned="right"` on the action column's
 * `<TableHead>` AND every matching `<TableCell>` and it stays parked against
 * the right edge while the rest of the columns scroll underneath — so Edit and
 * the bin are reachable without scrolling back.
 *
 * ⚠️ The background has to be genuinely OPAQUE or the columns sliding
 * underneath show straight through it. This is harder than it looks, because
 * `--muted` is ITSELF translucent in connect's dark theme
 * (`rgba(255,255,255,0.08)`); anything derived from it inherits that alpha:
 *
 *   • `bg-muted` / `bg-muted/50` — translucent, content shows through.
 *   • `color-mix(… var(--muted) 50%, var(--background))` — also translucent,
 *     since color-mix averages the ALPHA channels too (0.08 → ~0.54).
 *
 * So the hover tint is painted as a background-IMAGE layer on top of an opaque
 * `bg-background` colour. Two layers, composited by the browser exactly as the
 * row's own hover composites over the canvas — and opaque no matter what alpha
 * the token carries.
 *
 * A gradient strip to its left fades the incoming content into that same
 * colour, so it reads as flowing underneath rather than being cut off.
 *
 * The pinned cell paints `bg-background` (connect's page canvas). A table on a
 * different surface should pass its own, e.g.
 * `<TableCell pinned="right" className="bg-card">`.
 */
const PINNED_RIGHT_BASE =
  // No transition — see the note on `TableRow`. The tint is a background-image
  // layer, which can't animate, so the row doesn't animate either and the two
  // stay in lockstep.
  //
  // The row divider is drawn per-CELL (see `TableRow`), not on the `<tr>`, so
  // this cell gets it from there — only the left edge is its own.
  'sticky right-0 z-10 border-l border-border bg-background'

/**
 * The fade cast leftward from the pinned column, so content reads as passing
 * UNDER it rather than being abruptly chopped at the border. Same idiom as the
 * `Page` block's under-toolbar fade: dissolve into the surface colour, never a
 * `box-shadow` (the design law is flat — structure is hairlines and gradients,
 * not fog).
 *
 * `right-full` parks it immediately left of the cell; `sticky` already makes
 * the cell a containing block, so no extra `relative` is needed.
 */
const PINNED_RIGHT_FADE =
  'before:pointer-events-none before:absolute before:inset-y-0 before:right-full before:w-6 before:bg-gradient-to-l before:from-background before:to-transparent'

const PINNED_RIGHT = cn(PINNED_RIGHT_BASE, PINNED_RIGHT_FADE)

/**
 * The row's hover / selected tint, as a background-IMAGE layer so it sits on
 * top of the opaque `bg-background` colour instead of replacing it. Same
 * composite the row itself produces over the canvas, but it can't go
 * see-through however translucent `--muted` happens to be.
 *
 * Written out in full on purpose — Tailwind scans for literal class strings,
 * so a composed `group-hover/row:${TINT}` template would never be generated.
 */
const PINNED_RIGHT_CELL = cn(
  PINNED_RIGHT_BASE,
  PINNED_RIGHT_FADE,
  // Half-strength to match the row's `bg-muted/50`, and `in oklab` because
  // that is EXACTLY what Tailwind v4's `/50` modifier compiles to
  // (`color-mix(in oklab, <colour> 50%, transparent)`). Mixing in srgb instead
  // halves the alpha down a different curve and the pinned cell lands on a
  // visibly different shade from the row it sits in.
  'group-hover/row:bg-[image:linear-gradient(color-mix(in_oklab,var(--muted)_50%,transparent),color-mix(in_oklab,var(--muted)_50%,transparent))]',
  'group-data-[state=selected]/row:bg-[image:linear-gradient(var(--muted),var(--muted))]',
  // Fade OFF on a highlighted row. It dissolves into the canvas colour, so
  // against a tinted row it stops reading as "content passes under here" and
  // starts reading as a shadow smeared across the row. The tint already marks
  // the row; the border alone carries the column edge.
  'group-hover/row:before:opacity-0 group-data-[state=selected]/row:before:opacity-0'
)

function TableRow({
  className,
  children,
  ...props
}: React.ComponentProps<'tr'>) {
  const { pinLastColumn } = React.useContext(TableContext)
  const cells = React.Children.toArray(children)

  /**
   * Mark the last cell rather than making the host do it on every row. Skipped
   * for single-cell rows, which are the full-width empty / loading states
   * (`colSpan={n}`) — pinning those would freeze the whole message.
   */
  const pinnedChildren =
    pinLastColumn && cells.length > 1
      ? cells.map((cell, index) => {
          if (index !== cells.length - 1 || !React.isValidElement(cell)) {
            return cell
          }
          return React.cloneElement(
            cell as React.ReactElement<{ pinned?: 'right' }>,
            { pinned: 'right' }
          )
        })
      : children

  return (
    <tr
      data-slot="table-row"
      // `group/row` so a pinned cell can mirror the row's hover / selected
      // background — it can't inherit it, having to paint its own opaque one.
      // `bg-muted/50`, NOT the full token: `--muted` is an 8%-white wash in
      // dark mode and `--border` is ~6%, so a full-strength hover sits at
      // almost exactly the border's value and the row divider vanishes into it.
      // Half strength keeps the lines readable on a hovered row.
      //
      // NO `transition-colors`. The pinned last cell paints its tint as a
      // background-IMAGE (the only way to stay opaque over a translucent
      // token), and background-image cannot be transitioned — so a fading row
      // beside an instant pinned cell reads as lag every time you move down the
      // rows. Both snap together instead, which is what a data table wants.
      // The divider is `[&>*]:border-b` — drawn by every CELL, not by the
      // `<tr>`. A border on the row is painted at table level, but the pinned
      // last cell is `sticky` + `z-10` + opaque and paints above that layer, so
      // a row-level line rendered differently across the pinned column than
      // across the rest of the row (and the gap only showed on hover, when the
      // backgrounds behind each half diverged). Per-cell borders are drawn in
      // the same pass as each cell's own background, so the line is identical
      // all the way across in every state.
      className={cn(
        'group/row [&>*]:border-b [&>*]:border-border hover:bg-muted/50 data-[state=selected]:bg-muted',
        className
      )}
      {...props}
    >
      {pinnedChildren}
    </tr>
  )
}

function TableHead({
  className,
  pinned,
  ...props
}: React.ComponentProps<'th'> & {
  /** Freeze this column against the right edge while the rest scroll. */
  pinned?: 'right'
}) {
  return (
    <th
      data-slot="table-head"
      data-pinned={pinned}
      className={cn(
        // Muted, slightly roomier header cells — the Vercel-style quiet
        // header rather than stock shadcn's full-strength foreground.
        'h-10 px-3 text-left align-middle text-xs font-medium whitespace-nowrap text-muted-foreground [&:has([role=checkbox])]:pr-0',
        pinned === 'right' && PINNED_RIGHT,
        className
      )}
      {...props}
    />
  )
}

function TableCell({
  className,
  pinned,
  ...props
}: React.ComponentProps<'td'> & {
  /** Freeze this column against the right edge while the rest scroll. */
  pinned?: 'right'
}) {
  return (
    <td
      data-slot="table-cell"
      data-pinned={pinned}
      className={cn(
        'px-3 py-2.5 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0',
        pinned === 'right' && PINNED_RIGHT_CELL,
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

/**
 * TablePagination — the canonical prev / centre-label / next footer bar.
 * Rendered automatically by `<Table pagination={…}>`, and exported so a page
 * shell can host the same bar as a pinned page-level footer when needed.
 */
function TablePagination(props: TablePaginationProps) {
  const { className } = props

  let prevDisabled: boolean
  let nextDisabled: boolean
  let onPrev: () => void
  let onNext: () => void
  let center: React.ReactNode

  if (props.type === 'cursor') {
    prevDisabled = !props.hasPrev
    nextDisabled = !props.hasNext
    onPrev = props.onPrev
    onNext = props.onNext
    center = props.label ?? null
  } else {
    const { page, pageSize, total, onPageChange } = props
    const pageCount =
      props.pageCount ??
      (total != null ? Math.max(1, Math.ceil(total / pageSize)) : undefined)
    prevDisabled = page <= 1
    nextDisabled = pageCount != null ? page >= pageCount : false
    onPrev = () => {
      onPageChange(page - 1)
    }
    onNext = () => {
      onPageChange(page + 1)
    }
    if (total != null) {
      const from = total === 0 ? 0 : (page - 1) * pageSize + 1
      const to = Math.min(page * pageSize, total)
      center = `${from}–${to} of ${total}`
    } else if (pageCount != null) {
      center = `Page ${page} of ${pageCount}`
    } else {
      center = `Page ${page}`
    }
  }

  return (
    <div
      data-slot="table-pagination"
      className={cn(
        'flex items-center justify-between gap-2 px-4 py-3 text-sm text-muted-foreground',
        className
      )}
    >
      <Button
        variant="outline"
        size="sm"
        disabled={prevDisabled}
        onClick={onPrev}
      >
        Previous
      </Button>
      {center ? (
        <span className="text-xs tabular-nums">{center}</span>
      ) : (
        <span aria-hidden />
      )}
      <Button
        variant="outline"
        size="sm"
        disabled={nextDisabled}
        onClick={onNext}
      >
        Next
      </Button>
    </div>
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TablePagination,
}
export type {
  TablePaginationProps,
  TableCursorPagination,
  TableOffsetPagination,
}
